import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, computed, inject, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { take, tap, catchError, EMPTY } from 'rxjs';
import { DoubleClickDirective } from '@newroom-connect/library/directives';
import { ActionRole, ButtonType, FileType, Level } from '@newroom-connect/library/enums';
import { IFileHydrated, MediaItem } from '@newroom-connect/library/interfaces';
import { FileProcessingStatus } from '@newroom-connect/library/types';
import { FileProcessingService, FileService, MediaLibraryHelper } from '@newroom-connect/library/services';

import { CheckboxComponent } from '../../checkbox/checkbox.component';
import { MediaPreviewComponent } from '../../media-preview/media-preview.component';
import { DestroyableComponent } from '../../abstract/destroyable/destroyable.component';
import { ButtonComponent } from '../../buttons';
import { IconComponent } from '../../icon/icon.component';

@Component({
  selector: 'nrc-media-library-card-item',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MediaPreviewComponent,
    CheckboxComponent,
    ButtonComponent,
    IconComponent,
    DoubleClickDirective
  ],
  templateUrl: './media-library-card-item.component.html'
})
export class MediaLibraryCardItemComponent extends DestroyableComponent implements OnInit, OnChanges {
  private readonly fileService = inject(FileService);
  private readonly fileProcessingService = inject(FileProcessingService);

  @Input({ required: true }) public projectId!: string;
  @Input({ required: true }) public index!: number;

  @Input() public selectedItem: Partial<MediaItem> | null = null;
  @Input() public isFolderCard = true;
  @Input() public showCheckbox?: boolean;

  @Output() public itemClickEvent = new EventEmitter<{ index: number }>;
  @Output() public openCardEvent = new EventEmitter<{ index: number }>;

  public buttonType = ButtonType;
  public actionRole = ActionRole;
  public level = Level;

  public readonly thumbnailFile = signal<IFileHydrated | null>(null);
  public readonly fileItem = signal<IFileHydrated | null>(null);
  public readonly thumbnailFileSource = signal<string>('');
  public readonly fileItemSource = signal<string>('');

  public readonly isSelected = signal<boolean>(false);

  public readonly isExportingToPdf = signal<boolean>(false);
  public readonly isPdfExportSupported = computed(() => {
    const file = this.fileItem();

    if (!file) {
      return false;
    }

    return FileService.SUPPORTED_TRANSLATION_MIMETYPES.includes(file.mimetype);
  });

  public readonly hasExportedPdf = computed(() => {
    const file = this.fileItem();

    if (!file) {
      return false;
    }

    return file.formats?.some(format => format.type === FileType.PDF_EXPORT);
  });

  /**
   *
   */
  public ngOnInit(): void {
    if (!this.selectedItem) {
      return;
    }

    this.updateItemData(this.selectedItem);
  }

  /**
   *
   * @param changes
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedItem']?.currentValue && changes['selectedItem']?.currentValue !== changes['selectedItem']?.previousValue) {
      const current = changes['selectedItem'].currentValue;
      const previous = changes['selectedItem'].previousValue;

      this.isSelected.set(current.isSelected ?? false);

      // Only update if IDs or tags changed to avoid unnecessary API calls
      const shouldUpdate =
        current?.thumbnailId !== this.thumbnailFile()?.id ||
        ('fileId' in current && current.fileId !== this.fileItem()?.id) ||
        ('fileTags' in current && JSON.stringify(current.fileTags) !== JSON.stringify(previous?.fileTags));

      if (!shouldUpdate) {
        return;
      }

      this.updateItemData(current);
    }
  }

  /**
   *
   * @param index
   */
  public handleItemClickEvent(index: number): void {
    this.itemClickEvent.emit({ index });
  }

  /**
   *
   * @param index
   */
  public handleOpenCardEvent(index: number): void {
    this.openCardEvent.emit({ index });
  }

  /**
   *
   */
  public handleFileExportPdfEvent(): void {
    const cardItem = this.selectedItem;

    if (!cardItem || !MediaLibraryHelper.isFileItem(cardItem) || !cardItem.file) {
      return;
    }

    this.fileService.exportFileAsPdf(this.projectId, cardItem.file.id).pipe(
      take(1)
    ).subscribe((result) => {
      if (!result.sessionId || !result.success || !cardItem.file) {
        return;
      }

      this.isExportingToPdf.set(true);

      this.fileProcessingService.watchProgress(this.projectId, 'export-pdf', result.sessionId, cardItem.file.id)
        .pipe(
          tap(progress => {
            // Handle progress updates
            this.updateItemData({
              ...cardItem,
              fileId: cardItem.fileId
            });

            // Handle completion
            if (progress.status === FileProcessingStatus.COMPLETED) {
              this.isExportingToPdf.set(false);
              this.updateItemData({
                ...cardItem,
                fileId: cardItem.fileId
              });
            }
          }),
          catchError(() => {
            this.isExportingToPdf.set(false);
            return EMPTY;
          })
        )
        .subscribe();
    });
  }

  /**
   * Updates all item data when changes occur.
   *
   * @param item
   */
  private updateItemData(item: Partial<MediaItem>): void {
    if (!item) {
      return;
    }

    if (item.thumbnailId) {
      this.fileService.getFile(this.projectId, item.thumbnailId)
        .pipe(take(1))
        .subscribe(file => {
          this.thumbnailFile.set(file);
          this.thumbnailFileSource.set(file.source);
        });
    } else {
      this.thumbnailFile.set(null);
      this.thumbnailFileSource.set('');
    }

    if ('fileId' in item && item.fileId) {
      this.fileService.getFile(this.projectId, item.fileId)
        .pipe(take(1))
        .subscribe(file => {
          this.fileItem.set(file);
          this.fileItemSource.set(file.source);

          this.isExportingToPdf.set(file.queueJobs?.some(job => job.name === 'export-pdf' && job.status === 'ACTIVE') ?? false);
        });
    } else {
      this.fileItem.set(null);
      this.fileItemSource.set('');
    }
  }
}
